import { computed, type Component, type Ref } from 'vue';
import type { ItpDataGridColumn, GridFormatter } from './types';
import {
  columnKey,
  columnValueLookup,
  alignClass,
  isCommandColumn,
} from './gridCell';
import {
  resolveFormatter,
  registerBuiltinFormatters,
  collectionRenderer,
  isCollectionFormatter,
} from './formatters';

export interface GridRenderCell {
  key: string;
  class: unknown;
  component: Component | null;
  props: Record<string, any> | null;
  text: string;
  command: boolean;
}

interface GridCellsOptions {
  columns: Ref<ItpDataGridColumn[]>;
  formatNumber: (value: any) => string;
  formatDate: (value: any) => string;
}

// ── Cell rendering: resolve formatters once per column, then read each cell ──
export function useGridCells({
  columns,
  formatNumber,
  formatDate,
}: GridCellsOptions) {
  registerBuiltinFormatters();

  const resolvedSpecs = new Map<
    string,
    { spec: unknown; formatter: GridFormatter | null }
  >();

  const columnFormatters = computed(() => {
    const resolved = new Map<string, GridFormatter | null>();

    for (const column of columns.value) {
      const key = columnKey(column);
      const spec = column.formatter;
      const cached = resolvedSpecs.get(key);

      if (cached && cached.spec === spec) {
        resolved.set(key, cached.formatter);
        continue;
      }

      let formatter: GridFormatter | null;
      try {
        formatter = resolveFormatter(spec);
      } catch (error) {
        console.error(
          `[ItpDataGrid] column "${key}" could not resolve its formatter; rendering it unformatted.`,
          error,
        );
        formatter = null;
      }

      resolvedSpecs.set(key, { spec, formatter });
      resolved.set(key, formatter);
    }

    return resolved;
  });

  const valueFor = computed(() => columnValueLookup(columns.value));

  const collectionRenderers = new Map<
    string,
    { formatter: GridFormatter | null; renderer: Component | null }
  >();

  const columnCollections = computed(() => {
    const renderers = new Map<string, Component | null>();

    for (const column of columns.value) {
      const key = columnKey(column);
      const formatter = columnFormatters.value.get(key) ?? null;
      const cached = collectionRenderers.get(key);

      if (cached && cached.formatter === formatter) {
        renderers.set(key, cached.renderer);
        continue;
      }

      const renderer =
        formatter && isCollectionFormatter(formatter)
          ? collectionRenderer(formatter)
          : null;

      collectionRenderers.set(key, { formatter, renderer });
      renderers.set(key, renderer);
    }

    return renderers;
  });

  function formatterFor(column: ItpDataGridColumn): GridFormatter | null {
    return columnFormatters.value.get(columnKey(column)) ?? null;
  }

  function cellValue(row: any, column: ItpDataGridColumn): any {
    return valueFor.value(row, column);
  }

  function cellText(row: any, column: ItpDataGridColumn): string {
    const value = cellValue(row, column);
    const toText = formatterFor(column)?.toText;
    if (toText) {
      return toText(value, row);
    }
    if (value == null) {
      return '';
    }
    if (column.type === 'number') {
      return formatNumber(value);
    }
    if (column.type === 'date') {
      return formatDate(value);
    }
    return String(value);
  }

  function formatterText(
    row: any,
    column: ItpDataGridColumn,
  ): string | undefined {
    const toText = formatterFor(column)?.toText;
    return toText ? toText(cellValue(row, column), row) : undefined;
  }

  function isCommandCell(column: ItpDataGridColumn): boolean {
    return isCommandColumn(column) || formatterFor(column)?.command === true;
  }

  function buildCells(row: any): GridRenderCell[] {
    const read = valueFor.value;
    return columns.value.map((column) => {
      const key = columnKey(column);
      const value = read(row, column);
      const formatter = columnFormatters.value.get(key) ?? null;

      const collection = columnCollections.value.get(key) ?? null;
      const component =
        collection && Array.isArray(value)
          ? collection
          : (formatter?.component ?? null);

      const fixed = [alignClass(column), column.cellClass].filter(Boolean);
      const fromFormatter = formatter?.cellClass?.(value, row);

      const text = (() => {
        if (formatter?.toText) {
          return formatter.toText(value, row);
        }
        if (value == null) {
          return '';
        }
        if (column.type === 'number') {
          return formatNumber(value);
        }
        if (column.type === 'date') {
          return formatDate(value);
        }
        return String(value);
      })();

      const columnProps =
        typeof column.formatterProps === 'function'
          ? column.formatterProps(value, row)
          : column.formatterProps;

      const props = component
        ? collection && Array.isArray(value)
          ? { value, context: row, ...columnProps }
          : { value, ...formatter?.props?.(value, row), ...columnProps }
        : null;

      return {
        key,
        class: fromFormatter ? [...fixed, fromFormatter] : fixed,
        component,
        props,
        text,
        command: isCommandColumn(column) || formatter?.command === true,
      };
    });
  }

  return {
    formatterFor,
    cellValue,
    cellText,
    formatterText,
    isCommandCell,
    buildCells,
  };
}
